import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useState } from 'react';
import DataTable from 'components/DataTable';
import { fetchUsersSubscriptions } from 'services';
import { DATE_FORMATS, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import qs from 'query-string';
import { Loading } from 'components/loading/Loading';
import Filter from './Filter';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import AddSubscriptionModal from 'components/AddSubscriptionModal';
import CustomLink from 'components/TableLink';

let filters: { [p: string]: any; page: number } = {
  page: 0,
};

const columns = [
  {
    name: 'ID',
    selector: 'objectId',
    sortable: false,
    width: '120px',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.objectId}
        linkTo={`/subscriptions/${row.objectId}`}
      />
    ),
  },
  {
    name: 'User',
    selector: 'user.full_name',
    sortable: false,
  },
  {
    name: 'Subscription Name',
    selector: 'subscription.name',
    sortable: false,
  },
  {
    name: 'Type',
    selector: 'subscription.type',
    sortable: false,
    width: '110px',
  },
  {
    name: 'Quantity',
    selector: 'quantity',
    sortable: false,
    format: (row) => row.quantity ? row.quantity : '-',
  },
  {
    name: 'Last Box Sent At',
    selector: 'lastBoxSentAt',
    sortable: false,
    format: (row) => row.lastBoxSentAt ? formatDate(row.lastBoxSentAt, DATE_FORMATS.MM_DD_YYYY) : '-',
  },
  {
    name: 'Next Box',
    selector: 'nextBoxWillBeSentAt',
    sortable: false,
    format: (row) => row.nextBoxWillBeSentAt ? formatDate(row.nextBoxWillBeSentAt, DATE_FORMATS.MM_DD_YYYY) : '-',
  },
  {
    name: 'Status',
    selector: 'status',
    sortable: false,
    width: '110px',
  },
];

const UsersSubscriptions = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<Record<string, any>>({});
  const [subscriptionModalOpen, setAddSubscriptionModalOpen] = useState<boolean>(false);

  const fetchUsersSubscriptionsFn = async () => {
    setLoading(true);
    try {
      const response = await fetchUsersSubscriptions({
        ...filters,
        limit: getPageLimit(),
      });
      setSubscriptions(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };
  
  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    if (!props.data) {
      filters = {
        ...filters,
        ...queryStringToFilter(),
      };
      fetchUsersSubscriptionsFn();
    }
  }, []);
  
  const filterToQueryString = () => {
    const { filter } = filters;
    return qs.stringify({ page: filters.page, ...filter });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchUsersSubscriptionsFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    filters = { filter: updatedFilter, page: 0 };
    updateQueryString();
    fetchUsersSubscriptionsFn();
  };

  return (
    <div>
      {!props.data && (
        <div className={styles['header']}>
          <div className={styles['heading']}>Users Subscriptions</div>
          <ActionButton
            text={<>Subscription <span>+</span></>}
            active={true}
            isLoading={false}
            onClick={() => setAddSubscriptionModalOpen(true)}
          />
        </div>
      )}
      {!props.data && (
        <Filter {...props} onSubmit={onFilterSubmit} />
      )}
      {subscriptionModalOpen && (
        <AddSubscriptionModal
          toggleModal={() => setAddSubscriptionModalOpen(!subscriptionModalOpen)}
          onSuccess={fetchUsersSubscriptionsFn}
        />
      )}
      <DataTable
        columns={columns}
        noHeader={true}
        data={props.data || subscriptions.items}
        highlightOnHover={true}
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={props.total || subscriptions.total}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        loading={loading}
        loadingRowsCount={10}
      />
    </div>
  );
};

export { UsersSubscriptions };